/**
 * MessageList (06 §4.15) — virtualized (@tanstack/react-virtual) reverse-chron
 * scroll with sticky date separators, pinned E2EE banner at top, auto-stick on
 * new message when at bottom, ScrollToBottomFab otherwise. Items are a flattened
 * stream of {banner | date | message} rows so virtualization stays simple.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { isSameDay } from 'date-fns';
import type { Conversation, Message } from '../../data/model';
import { DateSeparator, EncryptionBanner } from './Pills';
import { MessageBubble } from './MessageBubble';
import { TypingBubble } from './TypingBubble';
import { ScrollToBottomFab } from './ScrollToBottomFab';
import styles from './MessageList.module.css';

type Row =
  | { kind: 'banner'; key: string }
  | { kind: 'date'; key: string; ms: number }
  | { kind: 'message'; key: string; message: Message; runStart: boolean }
  | { kind: 'typing'; key: string };

interface Props {
  conv: Conversation;
  messages: Message[];
  typing: boolean;
}

export function MessageList({ conv, messages, typing }: Props) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [showFab, setShowFab] = useState(false);
  const atBottomRef = useRef(true);

  const bySeq = useMemo(() => {
    const map = new Map<number, Message>();
    for (const m of messages) if (m.seq != null) map.set(m.seq, m);
    return map;
  }, [messages]);
  const resolveReply = (seq: number) => bySeq.get(seq);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [{ kind: 'banner', key: 'banner' }];
    let prev: Message | null = null;
    for (const m of messages) {
      if (!prev || !isSameDay(prev.sentAtMs, m.sentAtMs)) {
        out.push({ kind: 'date', key: `d-${m.clientMsgId}`, ms: m.sentAtMs });
      }
      const runStart =
        !prev ||
        prev.senderId !== m.senderId ||
        !isSameDay(prev.sentAtMs, m.sentAtMs) ||
        m.sentAtMs - prev.sentAtMs > 5 * 60_000;
      out.push({ kind: 'message', key: m.clientMsgId, message: m, runStart });
      prev = m;
    }
    if (typing) out.push({ kind: 'typing', key: 'typing' });
    return out;
  }, [messages, typing]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56,
    overscan: 12,
    getItemKey: (i) => rows[i]!.key,
  });

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    const el = parentRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  // Stick to bottom on mount + when new rows arrive while at bottom.
  useEffect(() => {
    if (atBottomRef.current) scrollToBottom('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  useEffect(() => {
    scrollToBottom('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.id]);

  const onScroll = () => {
    const el = parentRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    atBottomRef.current = distance < 120;
    setShowFab(distance > 120);
  };

  return (
    <div className={`${styles.list} wallpaper`} role="log" aria-live="polite" aria-label="Messages">
      <div ref={parentRef} className={styles.scroller} onScroll={onScroll}>
        <div className={styles.inner} style={{ height: virtualizer.getTotalSize() }}>
          {virtualizer.getVirtualItems().map((vi) => {
            const row = rows[vi.index]!;
            return (
              <div
                key={vi.key}
                ref={virtualizer.measureElement}
                data-index={vi.index}
                className={styles.row}
                style={{ transform: `translateY(${vi.start}px)` }}
              >
                {row.kind === 'banner' && <EncryptionBanner />}
                {row.kind === 'date' && <DateSeparator ms={row.ms} />}
                {row.kind === 'typing' && <TypingBubble />}
                {row.kind === 'message' && (
                  <MessageBubble
                    message={row.message}
                    conv={conv}
                    runStart={row.runStart}
                    resolveReply={resolveReply}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <ScrollToBottomFab
        visible={showFab}
        unread={conv.unreadCount}
        onClick={() => scrollToBottom('smooth')}
      />
    </div>
  );
}
